import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import {
  ChangeDetectionStrategy, Component, computed, effect,
  inject, input, model, signal, Type,
} from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { paramDeger, kutukGeo } from '@/src/app/shared/utils/param-deger.utils';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { DinamikBilesenRegistry } from '@service/fikoyt-ui/src/lib/dinamik-bilesen/dinamik-bilesen.registry';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { KutukStokEditComponent } from './kutuk-stok-edit.component';

type Durum = 'kritik' | 'uyari' | 'normal';
const DURUM_BILGI: Record<Durum, { etiket: string; renk: string; rgb: string }> = {
  kritik: { etiket: 'Kritik', renk: '#ef4444', rgb: '239,68,68' },
  uyari: { etiket: 'Uyarı', renk: '#f59e0b', rgb: '245,158,11' },
  normal: { etiket: 'Normal', renk: '#22c55e', rgb: '34,197,94' },
};

type Tip = 'giris' | 'cikis' | 'sayim' | 'fire';
const TIP_BILGI: Record<Tip, { etiket: string; simge: string; renk: string; isaret: string }> = {
  giris: { etiket: 'Giriş', simge: '⬆', renk: '#22c55e', isaret: '+' },
  cikis: { etiket: 'Çıkış', simge: '⬇', renk: '#ef4444', isaret: '−' },
  sayim: { etiket: 'Sayım', simge: '⚖', renk: '#3b82f6', isaret: '=' },
  fire: { etiket: 'Fire', simge: '⚠', renk: '#f59e0b', isaret: '−' },
};

// ---------------------------------------------------------------------------
// Ana stok kartı — alt hareketleri dinamik bileşen (ngComponentOutlet) ile render eder
// ---------------------------------------------------------------------------
@Component({
  selector: 'kutuk-stok-kart',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ksk" [style.--c]="renk()">
      <!-- Başlık + durum rozeti -->
      <div class="ksk-ust">
        <span class="ksk-ad">🪵 {{ d('isim') || 'İsimsiz Kütük' }}</span>
        <span class="ksk-rozet"
          [style.background]="'rgba('+rgb()+',.15)'"
          [style.color]="renk()">{{ DURUM_BILGI[durum()].etiket }}</span>
      </div>

      <!-- Ölçüler: en · boy · yükseklik çipleri (büyükten küçüğe sıralı) -->
      @if (olculer().length) {
        <div class="ksk-olculer">
          <span class="ksk-olcu" [style.--oc]="'blue'">
          @for (o of olculer(); track o.etiket) {
            
              <span class="ksk-olcu-simge">{{ o.simge }}</span>
              <b>{{ o.deger }}</b>
           
          }
         </span>
        </div>
      }

      <!-- Kütük SVG -->
      <div class="ksk-gorsel">
        <svg [attr.viewBox]="g().vb" preserveAspectRatio="xMidYMid meet" class="ksk-svg">
          <defs>
            <linearGradient id="kskUst" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stop-color="#fbbf24"/><stop offset="100%" stop-color="#f59e0b"/>
            </linearGradient>
            <linearGradient id="kskOn" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#d97706"/><stop offset="100%" stop-color="#b45309"/>
            </linearGradient>
            <linearGradient id="kskSag" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#b45309"/><stop offset="100%" stop-color="#92400e"/>
            </linearGradient>
          </defs>
          <polygon [attr.points]="g().on"  fill="url(#kskOn)"/>
          <polygon [attr.points]="g().sag" fill="url(#kskSag)"/>
          <polygon [attr.points]="g().ust" fill="url(#kskUst)"/>
          <polygon [attr.points]="g().on"  fill="none" stroke="#92400e" stroke-width="1" stroke-linejoin="round"/>
          <polygon [attr.points]="g().sag" fill="none" stroke="#78350f" stroke-width="1" stroke-linejoin="round"/>
          <polygon [attr.points]="g().ust" fill="none" stroke="#a16207" stroke-width="1" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Kalan stok + hızlı ekle/çıkar kısayolu -->
      <div class="ksk-hizli">
        <span class="ksk-miktar">{{ kalanStok() }} <span class="ksk-birim">{{ d('birim') || 'adet' }}</span></span>
        <span class="ksk-hizli-sag">
          <input class="ksk-hizli-adet" type="number" min="1" [value]="hizliMiktar()"
            (input)="hizliMiktar.set(Math.max(1, +$any($event.target).value || 1))"
            [disabled]="_kaydediyor()" title="Hızlı hareket miktarı">
          <button class="ksk-hbtn ksk-eksi" (click)="hizliHareket('cikis')"
            [disabled]="_kaydediyor()" title="Hızlı çıkarma (alt hareket olarak kaydeder)">−</button>
          <button class="ksk-hbtn ksk-arti" (click)="hizliHareket('giris')"
            [disabled]="_kaydediyor()" title="Hızlı ekleme (alt hareket olarak kaydeder)">+</button>
        </span>
      </div>
      <div class="ksk-bar"><div class="ksk-bar-ic" [style.width.%]="barYuzde()" [style.background]="renk()"></div></div>
      <div class="ksk-limit-etiket">kritik {{ d('kritikLimit') || '—' }} · uyarı {{ d('uyariLimit') || '—' }}</div>

      <!-- Alt hareketler — dinamik bileşen (paylasilmis_kart_sade) -->
      @if (altHareketler().length) {
        <div class="ksk-alt-baslik">Son Hareketler</div>
        <div class="ksk-alt-liste">
          @for (altKart of altHareketler(); track altKart._id) {
            @if (altBilesen()) {
              <ng-container
                [ngComponentOutlet]="altBilesen()!"
                [ngComponentOutletInputs]="{ parametreler: altKart }">
              </ng-container>
            }
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .ksk { display:flex; flex-direction:column; gap:6px; padding:10px 12px;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:10px; background:var(--py-arkaplan,#fff); }
    .ksk-ust { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .ksk-ad { font-size:13px; font-weight:700; color:var(--py-metin,#333); }
    .ksk-rozet { padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700;
      text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
    .ksk-gorsel { display:flex; justify-content:center; }
    .ksk-svg { display:block; width:100%; height:64px; }
    .ksk-miktar { font-size:20px; font-weight:800; color:var(--py-metin,#222); }
    .ksk-birim { font-size:11px; font-weight:500; color:var(--py-metin-ikincil,#888); }
    .ksk-bar { height:5px; border-radius:3px; background:rgba(0,0,0,.06); overflow:hidden; }
    .ksk-bar-ic { height:100%; border-radius:3px; transition:width .5s; }
    .ksk-limit-etiket { font-size:10px; color:var(--py-metin-ucuncul,#999); }
    .ksk-alt-baslik { font-size:10px; font-weight:700; text-transform:uppercase;
      letter-spacing:.06em; color:var(--py-metin-ucuncul,#999); margin-top:4px; }
    .ksk-alt-liste { display:flex; flex-direction:column; gap:4px; }
    .ksk-hizli { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .ksk-hizli-sag { display:flex; align-items:center; gap:4px; }
    .ksk-olculer { display:flex; flex-wrap:wrap; gap:4px; }
    .ksk-olcu { display:inline-flex; align-items:center; gap:4px; padding:2px 8px;
      font-size:11px; font-weight:500; color:var(--py-metin,#444);
      background:color-mix(in srgb, var(--oc,#64748b) 8%, transparent);
      border:1px solid color-mix(in srgb, var(--oc,#64748b) 25%, transparent);
      border-radius:999px; line-height:1.4; }
    .ksk-olcu b { font-weight:700; }
    .ksk-olcu-simge { font-size:11px; opacity:.85; }
    .ksk-hizli-adet { width:46px; padding:2px 4px; font-size:12px; text-align:center;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px;
      background:var(--py-arkaplan,#fff); color:var(--py-metin,#333); }
    .ksk-hbtn { width:26px; height:26px; border:none; border-radius:8px; cursor:pointer;
      font-size:15px; font-weight:800; line-height:1; color:#fff;
      display:flex; align-items:center; justify-content:center;
      transition:filter .15s, transform .05s; }
    .ksk-hbtn:hover:not(:disabled) { filter:brightness(1.12); }
    .ksk-hbtn:active:not(:disabled) { transform:scale(.92); }
    .ksk-hbtn:disabled { opacity:.45; cursor:not-allowed; }
    .ksk-arti { background:#22c55e; }
    .ksk-eksi { background:#ef4444; }
  `],
})
export class KutukStokKartComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  _hesap    = input<any>();

  constructor() {
    super();
    // Alt bileşen tipini bir kez yükle
    effect(() => {
      DinamikBilesenRegistry.goruntumAl('kutuk-stok-hareket', 'paylasilmis_kart_sade')
        .then(tip => this._altBilesen.set(tip));
    }, { allowSignalWrites: true });
    // Her kart görüntülendiğinde DB'den CANLI alt hareketler çekilir. Alt kartlar
    // silinse de üst kartta bayat/denormalize _hesap gösterilmesin — taze veri esas alınır.
    effect(() => {
      const kart = this.parametreler();
      const id = kart?._id || kart?.kart?._id;
      if (id) this._hesapCek(id);
    });
  }

  private _altBilesen = signal<Type<any> | null>(null);
  altBilesen = this._altBilesen.asReadonly();
  private _hesapYerel = signal<any>(null);
  private _ekleIstek: FikoYTIstekKok | null = null;

  /** Hızlı ekle/çıkar kısayolu: miktar + kaydediliyor durumu */
  protected readonly Math = Math; // template'te Math.max için
  hizliMiktar = signal(1);
  _kaydediyor = signal(false);

  d(ismi: string) { return paramDeger(this.parametreler()?.ek, ismi); }
  g = computed(() => kutukGeo(+(this.d('en') || 0), +(this.d('boy') || 0), +(this.d('yukseklik') || 0)));

  /** Ölçü çipleri: en / boy / yükseklik — yalnızca dolu olanlar, büyükten küçüğe sıralı */
  olculer = computed(() => {
    const birim = this.d('birim') || '';
    const sayi = (v: any) => {
      const n = +(v || 0);
      return n > 0 ? (Math.round(n * 100) / 100).toString() : '';
    };
    const liste = [
      { etiket: 'En',        simge: '↔',  n: +(this.d('en') || 0),        deger: sayi(this.d('en')),        renk: '#0ea5e9', birim },
      { etiket: 'Boy',       simge: '📏', n: +(this.d('boy') || 0),       deger: sayi(this.d('boy')),       renk: '#8b5cf6', birim },
      { etiket: 'Yükseklik', simge: '⤒',  n: +(this.d('yukseklik') || 0), deger: sayi(this.d('yukseklik')), renk: '#10b981', birim },
    ];
    return liste
      .filter(o => o.deger !== '')
      .sort((a, b) => b.n - a.n); // en büyük ölçü önce
  });

  /** _hesap'un birleşik görünümü: DB'den çekilen CANLI veri (silinenler dahil senkron)
   *  birincil, denormalize _hesap yalnızca canlı veri gelene kadar geçici gösterilir. */
  hesapYerel = computed(() => {
    const p = this.parametreler();
    const gelen = p?.kart?._hesap ?? this._hesap() ?? p?._hesap ?? {};
    const canli = this._hesapYerel();
    if (canli != null) return canli;            // canlı veri varsa esas al
    return gelen || {};                          // canlı henüz gelmediyse denormalize yedek
  });

  /** Hızlı ekle/çıkar: giris/cikis alt hareket kartını DB'ye yazar ve canlı hesabı tazeler */
  async hizliHareket(tip: 'giris' | 'cikis') {
    const p = this.parametreler();
    const parentId = p?._id || p?.kart?._id;
    if (!parentId || this._kaydediyor()) return;
    const miktar = Math.max(1, +(this.hizliMiktar() || 0));
    this._kaydediyor.set(true);
    try {
      const aciklama = tip === 'giris' ? 'Hızlı ekleme (+)' : 'Hızlı çıkarma (−)';
      const sahip = (this.FikoYT as any)?.kimlik?.aktifKullaniciJson()?._id;
      const kartKimlik = this.FikoYT.fikoIdUret();
      const icerikKimlik = this.FikoYT.fikoIdUret();

      const yeniKart: any = {
        _id: kartKimlik, vt: 'FikoYT.sistem.hareketler', yol: 'FikoYT.sistem.hareketler.kalem',
        turu: 'KART', gizlilik: 'Herkese Açık', _uid: parentId, sahip, tarih: new Date(),
        veri: {
          solSimge: { baslik: tip === 'giris' ? '⬆' : '⬇', renkArka: tip === 'giris' ? '#22c55e' : '#ef4444', renkYazi: '#fff' },
          icerikler: [icerikKimlik],
        },
      };
      const yeniIcerik: any = {
        _id: icerikKimlik, _uid: kartKimlik, html: '', aciklama: '', sifre: null,
        ek: {
          turu: 'UYGULAMAICERIK', _secilenSetId: 'kutuk-stok.hareket', bilesen: 'kutuk-stok-hareket',
          parametreler: [
            { ismi: 'tip', degeri: tip },
            { ismi: 'miktar', degeri: miktar },
            { ismi: 'aciklama', degeri: aciklama },
          ],
          tip, miktar, aciklama,
        },
      };

      if (!this._ekleIstek) {
        this._ekleIstek = new FikoYTIstekKok({
          metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle',
          onEkOlsun: true, depoModu: false, oncelik: 0,
          gecikmeliCevap: true, gecikmeliCevapSuresi: 500,
        });
      }
      const fdKart = new FormData(); fdKart.append('req_body', JSON.stringify(yeniKart));
      this._ekleIstek.parametreler = fdKart;
      await this._ekleIstek.isteSync();
      const fdIc = new FormData(); fdIc.append('req_body', JSON.stringify(yeniIcerik));
      this._ekleIstek.parametreler = fdIc;
      await this._ekleIstek.isteSync();

      // Canlı hesabı tazele (kalan stok + son hareketler anında güncellenir)
      this._hesapYerel.set(null);
      this._hesapCek(parentId);
    } catch (e) {
      console.warn('[KutukStok] Hızlı hareket kaydedilemedi:', parentId, e);
    } finally {
      this._kaydediyor.set(false);
    }
  }

  private kalanHesapYok(): boolean {
    const h = this.hesapYerel();
    return h?.kalanStok == null && h?.altHareketler == null;
  }

  kalanStok = computed(() => {
    const h = this.hesapYerel().kalanStok;
    return h != null ? Math.round(h * 100) / 100 : 0;
  });

  /** Alt hareket kartlarını prepipe'tan al (hesap.altHareketler) */
  altHareketler = computed<any[]>(() => {
    const raw = this.hesapYerel().altHareketler ?? ([] as any[]);
    // Sıralı stok birikimini koruyarak sayımların fark kaydını araya ekle,
    // ancak fire/giriş zaten gerçek alt-kart olarak kaydedildiği için burada
    // yalnızca görsel fark satırı (DB'ye yazmadan) gösterilir:
    return raw;
  });

  /** Prepipe/persist _hesap gelmediyse DB'den taze aggregate ile kalan + son 5 hareketi çek */
  private async _hesapCek(kartId: string) {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = { aggregate: [
        { $match: { _id: kartId } },
        { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
          { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
          { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
          { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
          { $match: { 'ic.ek._secilenSetId': 'kutuk-stok.hareket' } },
          { $sort: { _id: 1 } },
          { $project: {
            _id: '$ic._id',
            tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
            miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
            aciklama: { $ifNull: ['$ic.ek.aciklama', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'aciklama'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', ''] } } } ] },
            ek: '$ic.ek',
          } },
        ], as: '_alt' } },
        { $addFields: {
          kalanStok: {
            $reduce: {
              input: '$_alt', initialValue: 0,
              in: { $switch: { branches: [
                { case: { $eq: ['$$this.tip', 'giris'] }, then: { $add: ['$$value', '$$this.miktar'] } },
                { case: { $in: ['$$this.tip', ['cikis', 'fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
                { case: { $eq: ['$$this.tip', 'sayim'] }, then: '$$value' },
              ], default: '$$value' } } },
          },
          altHareketler: { $slice: ['$_alt', -5] },
        } },
        { $project: { _alt: 0 } },
      ] };
      const cevap: any = await istek.isteSync();
      const hesap = cevap?.cevap?.sonuc?.[0];
      if (hesap && (hesap.kalanStok != null || hesap.altHareketler)) {
        this._hesapYerel.set({ kalanStok: hesap.kalanStok ?? 0, altHareketler: hesap.altHareketler ?? [] });
      }
    } catch {}
  }

  durum = computed<Durum>(() => {
    const kritik = +(this.d('kritikLimit') || 0);
    const uyari  = +(this.d('uyariLimit')  || 0);
    const kalan  = this.kalanStok();
    if (kritik && kalan <= kritik) return 'kritik';
    if (uyari  && kalan <= uyari)  return 'uyari';
    return 'normal';
  });

  DURUM_BILGI = DURUM_BILGI;
  renk = computed(() => DURUM_BILGI[this.durum()].renk);
  rgb  = computed(() => DURUM_BILGI[this.durum()].rgb);

  barYuzde = computed(() => {
    const uyari = +(this.d('uyariLimit') || 0);
    const tavan = uyari > 0 ? uyari * 1.6 : Math.max(this.kalanStok(), 1);
    return Math.max(4, Math.min(100, (this.kalanStok() / tavan) * 100));
  });
}

// ---------------------------------------------------------------------------
// kutuk-stok DinamikBilesen kaydı
// prepipe: kalanStok hesabı + son 5 hareketin ek bilgisini altHareketler'e taşır
// ---------------------------------------------------------------------------
@DinamikBilesen({
  id: 'kutuk-stok',
  baslik: 'Kütük Stok',
  aciklama: 'Kütük stok kalemi ve kalan miktar takibi',
  ikon: 'inbox',
  kategori: 'kutuk-stok',
})
class KutukStok {
  paylas_kutusu     = KutukStokEditComponent;
  paylasilmis_kart      = KutukStokKartComponent;
  paylasilmis_kart_sade = KutukStokKartComponent;

  hesaplamalar = {
    prepipe: [
      { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
        { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
        { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
        { $match: { 'ic.ek._secilenSetId': 'kutuk-stok.hareket' } },
        { $sort: { _id: 1 } },
        { $project: {
          _id: '$ic._id',
          tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
          miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
          aciklama: { $ifNull: ['$ic.ek.aciklama', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'aciklama'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', ''] } } } ] },
          // ngComponentOutletInputs'a geçecek şekilde ek objesini koru
          ek: '$ic.ek',
        } },
      ], as: '_altHareket' } },
      { $addFields: {
        '_hesap.kalanStok': {
          $reduce: {
            input: '$_altHareket',
            initialValue: 0,
            in: { $switch: { branches: [
              { case: { $eq: ['$$this.tip', 'giris'] },   then: { $add:      ['$$value', '$$this.miktar'] } },
              { case: { $in: ['$$this.tip', ['cikis','fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
              { case: { $eq: ['$$this.tip', 'sayim'] },   then: '$$value' },
            ], default: '$$value' } },
          },
        },
        '_hesap.sonHareketTipi': { $ifNull: [{ $arrayElemAt: ['$_altHareket.tip', -1] }, null] },
        // Son 5 hareketi alt kart olarak taşı
        '_hesap.altHareketler': { $slice: ['$_altHareket', -5] },
      } },
      { $project: { _altHareket: 0 } },
    ],
  };
}

// ---------------------------------------------------------------------------
// Hareket sade kartı
// ---------------------------------------------------------------------------
@Component({
  selector: 'kutuk-stok-hareket-kart-sade',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="khk" [style.--tr]="bilgi().renk">
      <span class="khk-ikon">{{ bilgi().simge }}</span>
      <span class="khk-tip">{{ bilgi().etiket }}</span>
      <span class="khk-miktar">{{ miktarYazimi() }}</span>
      @if (d('aciklama')) { <span class="khk-aciklama">{{ d('aciklama') }}</span> }
    </div>
  `,
  styles: [`
    .khk { display:flex; align-items:center; gap:8px; padding:6px 10px;
      border-left:3px solid var(--tr,#94a3b8); border-radius:0 8px 8px 0;
      background:color-mix(in srgb, var(--tr) 6%, transparent); }
    .khk-ikon   { font-size:16px; }
    .khk-tip    { font-size:13px; font-weight:700; color:var(--tr,#333); text-transform:uppercase; letter-spacing:.5px; }
    .khk-miktar { font-size:13px; font-weight:700; font-family:monospace; color:var(--py-metin,#333); }
    .khk-aciklama { font-size:12px; color:var(--py-metin-ikincil,#666); margin-left:auto; }
  `],
})
export class KutukStokHareketKartSadeComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  constructor() { super(); }
  p    = computed(() => this.parametreler()?.ek || {});
  d(alan: string): any { return paramDeger(this.p(), alan); }
  /** Kalan-stok hesabıyla (aggregate `$ic.ek.tip`) birebir aynı kaynaktan tip okunur:
   *  önce düz `ek.tip`, yoksa fallback parametreler[]. Buna ek randım. */
  tip = computed(() => {
    const p = this.parametreler();
    const ek = p?.ek || {};
    const d = ek?.tip != null && ek?.tip !== '' ? ek.tip : paramDeger(ek, 'tip');
    return (d || 'giris') as Tip;
  });
  bilgi = computed(() => TIP_BILGI[this.tip()] || TIP_BILGI.giris);
  /** Miktar: çıkış/fire eksi, giriş artı, sayım işaretsiz gösterilir */
  miktarYazimi = computed(() => {
    const m = +(this.d('miktar') || 0);
    const t = this.tip();
    if (t === 'cikis' || t === 'fire') return `-${m}`;
    if (t === 'giris') return `+${m}`;
    return `${m}`; // sayim
  });
}

// ---------------------------------------------------------------------------
// Hareket edit bileşeni (paylaş kutusu)
// ---------------------------------------------------------------------------
@Component({
  selector: 'kutuk-stok-hareket-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="khe">
      <div class="khe-baslik">🔄 Hareket Tipi</div>
      <div class="khe-butonlar">
        @for (t of tipler; track t) {
          <button class="khe-btn" [class.khe-aktif]="p()?.tip === t"
            [style.--tr]="TIP_BILGI[t].renk" (click)="sec(t)">
            {{ TIP_BILGI[t].simge }} {{ TIP_BILGI[t].etiket }}
          </button>
        }
      </div>
      <div class="khe-row">
        <div class="khe-alan" style="flex:0 0 140px">
          <label class="khe-etiket">Miktar</label>
          <input class="khe-inp" type="number" min="0"
            [ngModel]="p()?.miktar" (ngModelChange)="guncelle('miktar', $event)" placeholder="Miktar"/>
        </div>
        <div class="khe-alan" style="flex:1">
          <label class="khe-etiket">Açıklama</label>
          <input class="khe-inp" type="text"
            [ngModel]="p()?.aciklama || ''" (ngModelChange)="guncelle('aciklama', $event)"
            placeholder="Açıklama (isteğe bağlı)"/>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .khe { display:flex; flex-direction:column; gap:8px; padding:4px 0; }
    .khe-baslik { font-size:13px; font-weight:600; color:var(--py-metin-ucuncul,#999); }
    .khe-butonlar { display:flex; gap:4px; flex-wrap:wrap; }
    .khe-btn { padding:6px 12px; border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px;
      background:transparent; cursor:pointer; font-size:12px; font-weight:500;
      color:var(--py-metin-ikincil,#666); transition:all .15s;
      &:hover { border-color:var(--tr); color:var(--tr); } }
    .khe-aktif { border-color:var(--tr) !important; color:var(--tr) !important;
      background:color-mix(in srgb, var(--tr) 12%, transparent) !important; font-weight:700; }
    .khe-row { display:flex; gap:10px; }
    .khe-alan { display:flex; flex-direction:column; gap:2px; }
    .khe-etiket { font-weight:500; font-size:12px; color:var(--py-metin-ucuncul, rgba(0,0,0,.45)); }
    .khe-inp { padding:6px 8px; border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px;
      font-size:13px; background:var(--py-arkaplan,#fff); color:var(--py-metin,#333); }
  `],
})
export class KutukStokHareketEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  tipler: Tip[] = ['giris', 'cikis', 'sayim', 'fire'];
  TIP_BILGI = TIP_BILGI;
  constructor() { super(); }
  p = computed(() => this.parametreler()?.ek || {});

  /** Değeri hem düz alana hem ek.parametreler[] içindeki ilgili parametreye yazar.
   *  paramDeger(array öncelikli) ve Mongo lookup'lar ($ic.ek.tip) böylece tutarlı okur. */
  private parametreYaz(alan: string, deger: any) {
    const params = this.parametreler();
    if (!params?.ek) return;
    const ek = params.ek;
    ek[alan] = deger;
    if (Array.isArray(ek.parametreler)) {
      const mevcut = ek.parametreler.find((x: any) => x.ismi === alan);
      if (mevcut) mevcut.degeri = deger;
    }
    this.parametreler.set({ ...params });
  }

  sec(tip: Tip) { this.parametreYaz('tip', tip); }
  guncelle(alan: string, deger: any) { this.parametreYaz(alan, deger); }
}

// ---------------------------------------------------------------------------
// kutuk-stok-hareket DinamikBilesen kaydı
// ---------------------------------------------------------------------------
@DinamikBilesen({
  id: 'kutuk-stok-hareket',
  baslik: 'Kütük Stok Hareketi',
  aciklama: 'Giriş / Çıkış / Sayım / Fire hareketi',
  ikon: 'swap',
  kategori: 'kutuk-stok',
})
class KutukStokHareket {
  paylas_kutusu         = KutukStokHareketEditComponent;
  paylasilmis_kart_sade = KutukStokHareketKartSadeComponent;
}
